import {useCallback, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import {atomIconSizes} from '@s-ui/react-atom-icon'
import AtomToast, {atomToastAutoCloseTimes} from '@s-ui/react-atom-toast'
import Context from '@s-ui/react-context'
import MoleculeStepper, {moleculeStepperDesign} from '@s-ui/react-molecule-stepper'
import {filterBasicTypes} from '@adv-mt/mt-filter-basic'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close.js'
import IconCloseCircle from '@adv-mt/mt-svg-icons/lib/CloseCircleOutline.js'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import Step from './components/step/index.js'
import StepZero from './components/stepZero/index.js'
import {questionRenderers} from './stepRenderer/index.js'

const NO_OP = () => {}

const FormDealerRating = ({
  onSubmit = NO_OP,
  onSecondaryAction = NO_OP,
  tradeId,
  questions = [],
  textReviewSettings
}) => {
  const {i18n, domain} = useContext(Context)
  const {track, page} = useAnalyticsTrack()

  const [step, setStep] = useState(0)
  const [rating, setRating] = useState(0)
  const [options, setOption] = useState([])
  const [comment, setComment] = useState('')
  const [showNotificationRating, setShowNotificationRating] = useState(false)
  const [showError, setShowError] = useState(false)

  const delayToNextFrame = useDelayToNextFrame()

  const currentQuestion = questions[step - 1]
  const stepsCount = questions.length + 1

  const config = domain.get('config')

  const {
    characterLimits: {max: maxChars, min: minChars}
  } = textReviewSettings || {}

  const urlStaticsImages = config.get('URL_STATICS_IMAGES')
  const {TRACKING_EVENT_NAMES} = config.get('tracking')
  const {DEALER_RATING_STEP_PAGE_NAMES} = config.get('page')

  const imageUrl = `${urlStaticsImages}illustrations/feedback.svg`

  const getRatingTrackingData = useCallback(
    async step => {
      const trackingData = await domain.get('get_page_tracking_data_use_case').execute({
        page: DEALER_RATING_STEP_PAGE_NAMES?.[step]
      })

      return page(trackingData)
    },
    [DEALER_RATING_STEP_PAGE_NAMES, domain, page]
  )

  useEffect(() => {
    getRatingTrackingData(step)
  }, [step, getRatingTrackingData])

  const handleNext = () => {
    if (!rating) {
      setShowNotificationRating(true)
      return
    }
    setStep(prevState => prevState + 1)
  }

  const handleScoreButtonClick = async ({rating}) => {
    setRating(rating)
  }

  const isCommentValid = useCallback(
    commentText => {
      const trimmedComment = commentText.trim()

      return trimmedComment === '' || trimmedComment.length >= minChars
    },
    [minChars]
  )

  const handleSetComment = comment => {
    setComment(comment)
    if (isCommentValid(comment) && showError) {
      setShowError(false)
    }
  }

  const handleSubmit = async () => {
    if (!isCommentValid(comment)) {
      setShowError(true)
      return
    }

    onSubmit({rating, options, comment})

    delayToNextFrame(async () => {
      const dataFormRatingTrackingData = await domain.get('get_event_tracking_data_use_case').execute({
        event: TRACKING_EVENT_NAMES.USER_RATING_COMPLETED,
        rating,
        options,
        comment,
        tradeId
      })

      track(dataFormRatingTrackingData)
    })
  }

  const isLastStep = step === stepsCount
  const isStepZero = step === 0

  const colorButton = isLastStep ? atomButtonColors.ACCENT : atomButtonColors.PRIMARY
  const textButton = isLastStep ? i18n.t('FORM.SEND') : i18n.t('COMMON.NEXT')

  const feedbackSecondStepFeedbackOptions = questions[1].options.map(({id, title}) => ({
    id,
    label: title
  }))

  const isMultiSelection = questions[1].type === 'MULTISELECT'
  const typeFilter = isMultiSelection
    ? filterBasicTypes.MULTI_SELECTION_OUTLINED
    : filterBasicTypes.SINGLE_SELECTION_OUTLINED

  const renderQuestionContent = questionType => {
    const questionRenderer = questionRenderers[questionType] ?? null
    if (!questionRenderer) {
      return null
    }

    return questionRenderer.render({
      rating,
      onRatingChange: value => handleScoreButtonClick({rating: value}),
      typeSelection: typeFilter,
      options: feedbackSecondStepFeedbackOptions,
      onOptionsChange: ({value}) => {
        setOption(value)
      },
      selectedOptions: options,
      showError,
      comment,
      maxChars,
      placeholder: i18n.t('FORM.DEALER_RATING.STEP_COMMENTS.PLACEHOLDER'),
      textCharacters: i18n.t('FORM.DEALER_RATING.STEP_COMMENTS.HELP_TEXT'),
      errorText: i18n.t('FORM.DEALER_RATING.STEP_COMMENTS.ERROR_TEXT', {minChars}),
      onChange: (_e, {value}) => handleSetComment(value)
    })
  }

  return (
    <div className="mt-FormDealerRating">
      {isStepZero ? (
        <StepZero
          imageUrl={imageUrl}
          title={i18n.t('FORM.DEALER_RATING.STEP_0.TITLE')}
          setStep={setStep}
          ctaPrimaryText={i18n.t('COMMON.YES')}
          ctaSecondaryText={i18n.t('COMMON.NO')}
          ctaSecondaryAction={onSecondaryAction}
          ctaPrimaryAction={() => setStep(prevState => prevState + 1)}
        />
      ) : (
        <>
          <MoleculeStepper design={moleculeStepperDesign.COMPRESSED} steps={questions.length + 1} step={step} />
          <div className="mt-FormDealerRating-containerSteps">
            {isLastStep ? (
              <Step title={textReviewSettings.title}>{renderQuestionContent('FINAL')}</Step>
            ) : (
              <Step
                key={currentQuestion.question}
                title={currentQuestion.question}
                subtitle={i18n.t(`FORM.DEALER_RATING.STEP_${currentQuestion.type}.SUBTITLE`)}
              >
                {renderQuestionContent(currentQuestion.type)}
              </Step>
            )}
            <div className="mt-FormDealerRating-stepsButtons">
              {step !== 1 && (
                <AtomButton
                  design={atomButtonDesigns.OUTLINE}
                  color={atomButtonColors.PRIMARY}
                  shape={atomButtonShapes.CIRCULAR}
                  size={atomButtonSizes.LARGE}
                  onClick={() => setStep(prevState => prevState - 1)}
                  fullWidth
                >
                  {i18n.t('COMMON.PREVIOUS')}
                </AtomButton>
              )}
              <AtomButton
                design={atomButtonDesigns.SOLID}
                color={colorButton}
                shape={atomButtonShapes.CIRCULAR}
                size={atomButtonSizes.LARGE}
                onClick={step !== stepsCount ? handleNext : handleSubmit}
                fullWidth
              >
                {textButton}
              </AtomButton>
            </div>
            {!isLastStep && currentQuestion.skippable && !options.length > 0 && (
              <div className="mt-FormDealerRating-skipButton">
                <AtomButton
                  design={atomButtonDesigns.FLAT}
                  color={colorButton}
                  shape={atomButtonShapes.CIRCULAR}
                  size={atomButtonSizes.LARGE}
                  onClick={() => setStep(prevState => prevState + 1)}
                >
                  {i18n.t('COMMON.SKIP')}
                </AtomButton>
              </div>
            )}
          </div>
        </>
      )}

      {showNotificationRating && (
        <AtomToast
          autoCloseTime={atomToastAutoCloseTimes.medium}
          iconClose={<IconClose size={atomIconSizes.medium} />}
          onClose={() => setShowNotificationRating(false)}
          show={showNotificationRating}
        >
          <div className="mt-FormDealerRating-notification">
            <IconCloseCircle size="medium" />
            <p className="mt-FormDealerRating-notificationText">
              {i18n.t('FORM.DEALER_RATING.STEP_SCALE.NOTIFICATION_ERROR_TEXT')}
            </p>
          </div>
        </AtomToast>
      )}
      {showError && step === stepsCount && (
        <AtomToast autoCloseTime={atomToastAutoCloseTimes.medium} onClose={() => setShowError(false)} show={showError}>
          <div className="mt-FormDealerRating-notification">
            <p className="mt-FormDealerRating-notificationText">
              {i18n.t('FORM.DEALER_RATING.STEP_COMMENTS.NOTIFICATION_ERROR_TEXT')}
            </p>
          </div>
        </AtomToast>
      )}
    </div>
  )
}

FormDealerRating.displayName = 'FormDealerRating'
FormDealerRating.propTypes = {
  onSecondaryAction: PropTypes.func,
  onSubmit: PropTypes.func,
  tradeId: PropTypes.string,
  questions: PropTypes.arrayOf(PropTypes.object),
  textReviewSettings: PropTypes.object
}

export default FormDealerRating
